import { useEffect, useRef } from 'react';
import { usePlatform } from '@/platform';
import { useUiStore } from '@/shared/hooks/use-shallow-store';
import { SoundCue } from './types';

/** Document-level click delegation firing `SoundCue.ButtonTap`. Mounted once at app shell. */
const isSilenced = (el: Element): boolean => {
  if (el instanceof HTMLButtonElement && el.disabled) return true;
  if (el.getAttribute('aria-disabled') === 'true') return true;
  if (el.closest('[data-no-sound]')) return true;
  return false;
};

const matchesButton = (el: Element): Element | null => {
  return el.closest('button, [role="button"]');
};

export const useButtonSound = (): void => {
  // Ref pins latest values so the doc-level handler closure doesn't go stale.
  const muted = useUiStore((s) => s.muted);
  const volume = useUiStore((s) => s.volume);
  const audio = usePlatform().audio;
  const ref = useRef({ muted, volume, audio });
  ref.current = { muted, volume, audio };

  useEffect(() => {
    const handler = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const btn = matchesButton(target);
      if (!btn) return;
      if (isSilenced(btn)) return;
      if (ref.current.muted) return;
      ref.current.audio.play(SoundCue.ButtonTap, { volume: ref.current.volume });
    };
    document.addEventListener('click', handler, { capture: true });
    return () => {
      document.removeEventListener('click', handler, { capture: true });
    };
  }, []);
};
